import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  Box,
  Button,
  Collapse,
  Dialog,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import {
  MyTimesheetClientSelector,
  MyTimesheetCostSelector,
  MyTimesheetDivider,
  MyTimesheetNewRequestDialogDuration,
  MyTimesheetNewRequestDialogTimeDuration,
  MyTimesheetProjectSelector,
} from "my-timesheet/components";
import { MyTimesheetShowRequestDialogHeader } from "my-timesheet/components";
import * as Yup from "yup";

type EventType = {
  start: string | Date;
  end: string | Date;
  state?: string;
};

const ValidationSchema = (events: EventType[]) =>
  Yup.object().shape({
    name: Yup.string()
      .required("OC_NAME_REQUIRED")
      .test({
        name: "uniqueName",
        exclusive: false,
        message: "MY_TIMESHEET_NAME_ALREADY_EXISTS",
        test: function (value) {
          if (!value || !events) return true;

          const existingNames = events.map((event: any) =>
            event.title?.toLowerCase() || ""
          );

          return !existingNames.includes(value.toLowerCase());
        },
      }),
    description: Yup.string().max(255, "MY_TIMESHEET_NAME_TOO_LONG"),
    projectId: Yup.string(),
    clientId: Yup.string(),
    costId: Yup.string(),
    startTime: Yup.date()
      .required("OC_START_DATE_REQUIRED")
      .test({
        name: "startDateInFuture",
        exclusive: false,
        message: "OC_DATE_IN_FUTURE",
        test: function (value) {
          if (!value) return true;
          const today = moment().startOf("day");
          const selectedDate = moment(value).startOf("day");
          return selectedDate.isSameOrBefore(today);
        },
      }),
    endTime: Yup.date()
      .required("OC_END_DATE_REQUIRED")
      .test({
        name: "endDateBeforeStartDate",
        exclusive: false,
        message: "OC_END_DATE_BEFORE_START_DATE",
        test: function (value) {
          if (!value || !this.parent.startTime) return true;
          const start = moment(this.parent.startTime);
          const end = moment(value);
          return end.isSameOrAfter(start);
        },
      })
      .test({
        name: "endTimeNotBeforeStartTime",
        exclusive: false,
        message: "END_TIME_NOT_BEFORE_START_TIME",
        test: function (value) {
          if (!value || !this.parent.startTime) return true;
          const start = moment(this.parent.startTime);
          const end = moment(value);
          return end.isAfter(start) || end.isSame(start);
        },
      })
      .test({
        name: "overlappingTimePeriods",
        exclusive: false,
        message: "OC_OVERLAPPING_TIME_PERIODS",
        test: function (value) {
          if (!value || !this.parent.startTime || !events) return true;

          const recordStart = moment(this.parent.startTime);
          const recordEnd = moment(value);

          const relevantEvent = events.find((event) => {
            const eventStart = moment(event.start);
            const eventEnd = moment(event.end);

            return (
              recordStart.isBetween(eventStart, eventEnd, null, "[)") ||
              recordEnd.isBetween(eventStart, eventEnd, null, "(]") ||
              (recordStart.isSameOrBefore(eventStart) &&
                recordEnd.isSameOrAfter(eventEnd))
            );
          });

          if (relevantEvent?.state === "rejected") {
            return true;
          }

          return !events.some((event) => {
            const eventStart = moment(event.start);
            const eventEnd = moment(event.end);

            return (
              recordStart.isBetween(eventStart, eventEnd, null, "[)") ||
              recordEnd.isBetween(eventStart, eventEnd, null, "(]") ||
              (recordStart.isSameOrBefore(eventStart) &&
                recordEnd.isSameOrAfter(eventEnd))
            );
          });
        },
      }),
  });

type MyTimesheetNewRequestDialogProps = {
  open: boolean;
  resetNewRequest: () => void;
  createMyRequest: (args: { variables: any }) => Promise<any>;
  onClose: () => void;
  newRequest?: {
    name?: string;
    description?: string;
    projectId?: string;
    clientId?: string;
    costId?: string;
    startTime?: string | Date | null;
    endTime?: string | Date | null;
    state?: string;
  };
  events: EventType[];
  isMobile: boolean;
};

export const MyTimesheetNewRequestDialog = ({
  open,
  resetNewRequest,
  createMyRequest,
  onClose,
  newRequest,
  events,
  isMobile,
}: MyTimesheetNewRequestDialogProps) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: newRequest?.name || "",
      description: newRequest?.description || "",
      projectId: newRequest?.projectId || "",
      clientId: newRequest?.clientId || "",
      costId: newRequest?.costId || "",
      startTime: newRequest?.startTime ?? undefined,
      endTime: newRequest?.endTime ?? undefined,
      state: newRequest?.state || "",
    },
    initialStatus: null,
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: ValidationSchema(events),
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        formik.setStatus(null);
        await createMyRequest({ variables: values });
        resetNewRequest();
        onClose();
      } catch (error: any) {
        const code = error?.code || error?.message;
        if (code === "OC_DATE_TOO_FAR_AWAY") {
          formik.setStatus("OC_DATE_TOO_FAR_AWAY");
          return;
        }
        formik.setStatus("MY_TIMESHEET_CREATE_FAILED");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleReset = (e: React.FormEvent) => {
    e.preventDefault();
    formik.resetForm();
    resetNewRequest();
  };

  const errors = [
    ...Object.values(formik.errors).filter((e) => e !== ""),
    ...(formik.status ? [formik.status] : []),
  ];

  const formikDuration =
    formik.values.startTime && formik.values.endTime
      ? moment
        .utc(
          new Date(formik.values.endTime as string | Date).getTime() -
          new Date(formik.values.startTime as string | Date).getTime(),
        )
        .format("HH:mm")
      : "00:00";

  let disabled = false;

  if (formikDuration === "00:00" || isSubmitting) {
    disabled = true;
  }

  return (
    <Dialog open={open} fullScreen={isMobile ? true : false}>
      <Box
        width={isMobile ? "auto" : "568px"}
        id="new-request-dialog"
        maxWidth="568px"
        padding="0px 16px"
      >
        <MyTimesheetShowRequestDialogHeader
          label={t("MY_TIMESHEET_ADD_TIMESHEET")}
          close={resetNewRequest}
          isMobile={isMobile}
        />
        <MyTimesheetDivider width="2px" />
        <Box height="18px" />
        <TextField
          id="name"
          name="name"
          label={t("OC_NAME")}
          variant="outlined"
          value={formik.values.name}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText=" "
          onChange={formik.handleChange}
          onFocus={() => formik.setFieldError("name", "")}
          style={{
            width: isMobile ? "100%" : "568px",
          }}
        />
        <Box height="16px" />
        <MyTimesheetProjectSelector formik={formik} />
        <MyTimesheetClientSelector formik={formik} />
        <MyTimesheetCostSelector formik={formik} />
        <Box height="18px" />
        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          justifyContent="space-between"
          width={isMobile ? "auto" : "568px"}
          alignItems={isMobile ? "center" : "unset"}
        >
          <MyTimesheetNewRequestDialogTimeDuration
            formik={formik}
            isMobile={isMobile}
          />
          {isMobile && <Box height="16px" />}
          <MyTimesheetNewRequestDialogDuration formik={formik} />
        </Box>
        <Box width={isMobile ? "auto" : "568px"}>
          <Box height="32px" />
          <TextField
            id="outlined-multiline-static"
            label="Megjegyzés"
            name="description"
            multiline
            minRows={3}
            variant="outlined"
            fullWidth
            style={{ height: "auto" }}
            value={formik.values.description}
            error={
              formik.touched.description && Boolean(formik.errors.description)
            }
            helperText={
              (formik.touched.description && formik.errors.description) ||
              t("OC_OPTIONAL")
            }
            onChange={formik.handleChange}
            onFocus={() => formik.setFieldTouched("description", false)}
          />
          <Box height="18px" />
        </Box>
        <MyTimesheetDivider width="1px" />
        <Collapse in={true}>
          {errors.map((error, index) => (
            <React.Fragment key={index}>
              <Typography variant="body2" color="#CC0033">
                {t(error)}
              </Typography>
              {index === errors.length - 1 && <Box height="16px" />}
            </React.Fragment>
          ))}
        </Collapse>
        <Box
          display="flex"
          flexDirection={isMobile ? "column-reverse" : "row"}
          justifyContent="space-between"
        >
          <Button
            id="cancel-send-approval"
            variant="text"
            color="secondary"
            onClick={handleReset}
            style={{
              width: isMobile ? "100%" : "calc(50% - 8px)",
            }}
          >
            {t("OC_CANCEL")}
          </Button>
          {isMobile && <Box height="16px" />}
          <Button
            id="send-approval"
            onClick={() => formik.handleSubmit()}
            variant="contained"
            color="primary"
            disableElevation
            disabled={disabled}
            style={{
              width: isMobile ? "100%" : "calc(50% - 8px)",
            }}
          >
            {t("MY_TIMESHEET_SEND_APPROVAL")}
          </Button>
        </Box>
        <Box height="16px" />
      </Box>
    </Dialog>
  );
};

export default MyTimesheetNewRequestDialog;
